import React, {useEffect, useState, useCallback} from 'react';
import {sortBy as _sortBy} from 'lodash';
import PropTypes from 'prop-types';
import {Button, Header, Segment} from 'semantic-ui-react';

import {CircularProgress, Backdrop} from '@ui';
import Divider from '@ui/Divider';
import {hideModal} from '@utils/modal';
import EventContentMultiAudioForm from '@modules/events/forms/EventContentMultiAudioForm';
import {refetchQueryByName} from '@utils/apollo';
import {convertToInt} from '@utils/helpers';
import {showMessageBox} from '@utils/messageBox';
import {TableHeader} from '@modules/events/components/Table/EventContentMultiAudio/TableHeader';
import {EditableGrids} from '@modules/events/components/Table/EventContentMultiAudio/EditableGrids';

export const columns = {
    tracks: {label: 'Track', width: 3},
    channels: {label: 'Channels', width: 5},
    streamLanguage: {label: 'Language', width: 5},
    actions: {label: '', width: 3},
};

const getMultiAudioSupport = (targets) => {
    return targets?.[0]?.encoding_job_profile?.has_multi_audio_support || false;
};

const hasEmptyLanguage = (audios) => audios.some(audio => !audio.stream_language);

const refetchAllQueries = () => {
    [
        'EventContentMultiAudios',
        'GetEncodingTargetsForDistributions',
        'GetEventContentEncodingConfigurationChanged',
    ].forEach(refetchQueryByName);
};

const EventContentMultiAudioTable = ({...props}) => {
    const [activeEditableFields, setActiveEditableFields] = useState(null);
    const [audios, setAudios] = useState([]);
    const [streamLanguages, setStreamLanguages] = useState([]);
    const [encodingTargetsSr, setEncodingTargetsSr] = useState([]);
    const [loading, setLoading] = useState(false);
    const [encodingTargetId, setEncodingTargetId] = useState(null);


    useEffect(() => {
        const {audios, streamLanguages, encodingTargetsSr} = props.DataEventContentMultiAudio;
        setAudios(audios ? _sortBy(audios, 'creation_datetime') : []);
        setStreamLanguages(streamLanguages);
        setEncodingTargetsSr(encodingTargetsSr);
        setEncodingTargetId(props.encodingTargetId);
    }, [props?.DataEventContentMultiAudio]);

    const addMessage = useCallback((type, msg, isSticky = false) => {
        showMessageBox('eventAudioMessage', null, msg, type, isSticky);
    }, []);

    const showErrorMultiAudioNotSaved = (error) => {
        props.MessageBox.addMessage('eventAudioMessage', 'The audio could not be saved.', `${error}`, 'error');
        setLoading(false);
    };

    const handleMultiAudioAdd = () => {
        setLoading(true);
        const {audioToSave, CreateMutation, Form: {clearForm}} = props;
        const dataToSave = {
            encodingTargetId: convertToInt(encodingTargetId),
            trackNumber: convertToInt(audioToSave.trackNumber),
            channels: audioToSave.channels || [],
            streamLanguage: convertToInt(audioToSave.streamLanguage) || '',
        };

        if (hasEmptyLanguage(audios)) {
            addMessage('error', 'When multiple audios are configured, all of them must have a language defined.' +
                ' Please define a language for the existing audio before to be able to add a new one.', true);
            setLoading(false);

            return;
        }

        if (1 <= audios.length && !convertToInt(audioToSave.streamLanguage)) {
            addMessage('error', 'When multiple audios are configured, all of them must have a language defined.' +
                ' Please define a language for the new audio you want to add.', true);
            setLoading(false);

            return;
        }

        if (!getMultiAudioSupport(encodingTargetsSr) && 1 <= audios.length) {
            addMessage('error', 'Only one audio can be configured because the encoding job profile of this' +
                ' encoding target does not support multi-audio.', true);
            setLoading(false);

            return;
        }

        CreateMutation({variables: dataToSave})
            .then(() => {
                addMessage('success', 'The audio has been saved successfully.');
                refetchAllQueries();
                clearForm('form_ema_add');
                setLoading(false);
            })
            .catch(showErrorMultiAudioNotSaved);
    };

    const handleMultiAudioUpdate = () => {
        setLoading(true);
        const {UpdateMutation, audioToUpdate} = props;
        const dataToUpdate = {
            id: convertToInt(audioToUpdate.id),
            trackNumber: convertToInt(audioToUpdate.trackNumber),
            channels: audioToUpdate.channels.sort((a, b) => a - b),
            streamLanguage: convertToInt(audioToUpdate.streamLanguage) || '',
            encodingTargetId: convertToInt(encodingTargetId),
        };

        if (1 < audios.length && !convertToInt(audioToUpdate.streamLanguage)) {
            addMessage('error', 'When multiple audios are configured, all of them must have a language defined.' +
                ' Please define a language for the existing audio before to be able to add a new one.', true);
            setLoading(false);

            return;
        }

        UpdateMutation({variables: dataToUpdate})
            .then(() => {
                addMessage('success', 'The audio has been saved successfully.');
                refetchAllQueries();
                setLoading(false);
            })
            .catch(showErrorMultiAudioNotSaved);
    };

    return (
        <div>
            <Backdrop open={loading} inverted>
                <CircularProgress />
            </Backdrop>
            <Header content='Audio configuration'/>
            <TableHeader/>
            <EventContentMultiAudioForm
                form='form_ema_add'
                formData={{streamLanguages, audios, hasMultiAudioSupport: getMultiAudioSupport(encodingTargetsSr)}}
                onAdd={handleMultiAudioAdd}
                loading={loading}
            />
            {audios.map((data) => (
                <React.Fragment key={data.id}>
                    <Divider/>
                    {activeEditableFields === convertToInt(data.id)
                        ? <EventContentMultiAudioForm
                            form='form_ema_update'
                            loading={loading}
                            formData={{
                                streamLanguages,
                                audios: {
                                    id: data.id,
                                    trackNumber: data.track_number,
                                    channels: data.channels,
                                    streamLanguages: data.stream_language,
                                },
                                hasMultiAudioSupport: getMultiAudioSupport(encodingTargetsSr),
                            }}
                            onUpdate={handleMultiAudioUpdate}
                            onDeactivateEditableCell={() => setActiveEditableFields(null)}
                        />
                        : <EditableGrids
                            data={data}
                            encodingTargetId={encodingTargetId}
                            setActiveEditableFields={setActiveEditableFields}
                            {...props}
                        />}
                </React.Fragment>
            ))}
            <Segment className='--multiAudioModalFooter'>
                <Button onClick={hideModal} icon='close' content='Close'/>
            </Segment>
        </div>
    );
};

EventContentMultiAudioTable.propTypes = {
    DataEventContentMultiAudio: PropTypes.object,
    MessageBox: PropTypes.object,
    encodingTargetId: PropTypes.number,
    audioToSave: PropTypes.object,
    audioToUpdate: PropTypes.object,
    CreateMutation: PropTypes.func,
    DeleteMutation: PropTypes.func,
    UpdateMutation: PropTypes.func,
    Form: PropTypes.object,
};

export default EventContentMultiAudioTable;
